import type { Concept } from '@mathflat/domain/Concept'
import type { WrongWorksheetType } from '@mathflat/domain/MakeWorksheet'
import type { Student } from '@mathflat/domain/Student'
import { makeAutoObservable } from 'mobx'
import { MAX_PROBLEM_COUNT } from 'src/constant'

import type {
  ByWorksheetChapter,
  CommonDerivationByChapter,
} from '@/dto/Derivation/derivationParams'
import { typeSafeAssign } from '@/utils/common'

import type { WorksheetDerivationRequestOption } from './derivationOptions'

export type 틀린유형학습지_출제방식 = '유형수' | '정답률'

type 틀린유형학습지_유형수 = Omit<
  ByWorksheetChapter<'WRONG_CONCEPT_WORKSHEET', '유형수'>,
  'conceptIdList' | 'studentId'
> & {
  weakStandard: Extract<틀린유형학습지_출제방식, '유형수'>
}

type 틀린유형학습지_정답률 = Omit<
  ByWorksheetChapter<'WRONG_CONCEPT_WORKSHEET', '정답률'>,
  'conceptIdList' | 'studentId'
> & {
  weakStandard: Extract<틀린유형학습지_출제방식, '정답률'>
}

// 학생개인 - 단원취약유형 관리 - 학습지별 오답
export type 틀린유형학습지<T extends 틀린유형학습지_출제방식 = 틀린유형학습지_출제방식> = {
  concept: T extends '유형수' ? 틀린유형학습지_유형수 : 틀린유형학습지_정답률
}

type 틀린문제학습지 = {
  problem: Omit<ByWorksheetChapter<'WRONG_PROBLEM_WORKSHEET', never>, 'conceptIdList' | 'studentId'>
}

// TODO: 오답학습지 리팩토링 삭제
export class 단원별오답옵션
  implements 틀린유형학습지, 틀린문제학습지, WorksheetDerivationRequestOption<'OTHERS'>
{
  wrongWorksheetType: WorksheetDerivationRequestOption<'OTHERS'>['wrongWorksheetType'] =
    'WRONG_CONCEPT_WORKSHEET' // 오답 학습지 종류
  conceptIdList: CommonDerivationByChapter['conceptIdList'] = []
  studentId = ''

  concept: 틀린유형학습지['concept'] & {
    conceptCount?: number
    correctRatio?: number
    topWeakConceptIdList?: Concept['id'][]
  } = {
    // 공통
    problemCountPerUnit: 4, //  문제 수 - default 취약유형 = 4 / 정답률 기준 = 3
    problemRequestType: 'SIMILAR' as const, // 문제 종류
    onlyAutoScorable: true,
    excludePrevious: false,
    excludeOOC: false,
    preferPairOverSimilar: true,

    // 틀린유형 학습지
    levelType: 'NORMAL' as const, // 틀린유형 - 난이도
    topWeakConceptIdList: [], // 취약유형 - 취약유형 아이디 (순위 정렬)
    conceptCount: 5, // 취약유형 - 유형 수
    correctRatio: 30, // 취약유형 - 정답률

    weakStandard: '유형수' as const, // 틀린유형 - UI only
    isFillProblem: true, // 문제 갯수가 부족할 경우 다른 유형으로라도 요청 문제 갯수만큼 꽉 채울지 여부
  }

  problem: 틀린문제학습지['problem'] = {
    // 공통
    onlyAutoScorable: true,
    excludePrevious: false,
    excludeOOC: false,

    // 틀린문제 학습지
    problemRequestType: 'SAME' as const, // 문제 종류'
    isFillProblem: true, // 문제 갯수가 부족할 경우 다른 유형으로라도 요청 문제 갯수만큼 꽉 채울지 여부
    maxProblemCount: MAX_PROBLEM_COUNT, // 사용자가 요청한 최대 문제수
  }

  constructor({ studentId }: { studentId: Student['id'] }) {
    makeAutoObservable(this)
    this.studentId = studentId
  }

  update = (
    options?: Partial<
      | 틀린유형학습지['concept']
      | 틀린문제학습지['problem']
      | { wrongWorksheetType: 단원별오답옵션['wrongWorksheetType'] }
      | Pick<CommonDerivationByChapter, 'conceptIdList'>
    >,
  ) => {
    if (options) {
      if ('wrongWorksheetType' in options) {
        this.wrongWorksheetType = options.wrongWorksheetType!
        delete options.wrongWorksheetType
      }

      if ('conceptIdList' in options) {
        this.conceptIdList = options.conceptIdList as CommonDerivationByChapter['conceptIdList']
        delete options.conceptIdList
      }

      if (this.wrongWorksheetType === 'WRONG_CONCEPT_WORKSHEET') {
        typeSafeAssign(this.concept, options)
      } else {
        typeSafeAssign(this.problem, options)
      }
    }
  }

  get params(): ByWorksheetChapter<WrongWorksheetType> {
    if (!this.conceptIdList || !this.studentId) throw Error('필수값이 없습니다.')

    const baseParams = {
      conceptIdList: this.conceptIdList,
      studentId: this.studentId,
    }

    if (this.wrongWorksheetType === 'WRONG_CONCEPT_WORKSHEET') {
      const {
        weakStandard,
        conceptCount,
        correctRatio,
        topWeakConceptIdList,
        preferPairOverSimilar,
        ...rest
      } = this.concept //UI값 제거
      // [levelType과 preferPairOverSimilar사이의 로직 존재]
      // levelType === 'NORMAL'일경우 preferPairOverSimilar는 boolean,
      // 나머지 경우, preferPairOverSimilar는 false 혹은 undefined
      // 타입으로 해결하려했으나 타입이 너무 복잡해져서 오히려 가독성 떨어뜨려서 제외. 로직 파악시 참고 부탁드립니다.

      let _preferPairOverSimilar = preferPairOverSimilar
      if (this.concept.levelType !== 'NORMAL') {
        _preferPairOverSimilar = undefined
      }
      if (weakStandard === '유형수') {
        return {
          ...baseParams,
          ...rest,
          conceptCount,
          topWeakConceptIdList,
          preferPairOverSimilar: _preferPairOverSimilar,
        } as ByWorksheetChapter<'WRONG_CONCEPT_WORKSHEET', '유형수'>
      } else {
        return {
          ...baseParams,
          ...rest,
          correctRatio,
          preferPairOverSimilar: _preferPairOverSimilar,
        } as ByWorksheetChapter<'WRONG_CONCEPT_WORKSHEET', '정답률'>
      }
    }
    return { ...baseParams, ...this.problem } as ByWorksheetChapter<'WRONG_PROBLEM_WORKSHEET'>
  }
}
